import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import lodashCompact from 'lodash/fp/compact';

import {
  getListingFieldsForDisplay,
  getMarketSecurityDepositLabel,
} from 'common/listingHelpers';

import {
  numberWithCommas,
  bedsDisplayString,
  bathsDisplayString,
} from 'common/formatters';

import {
  hasRoleAnonymous,
  hasRoleAdmin,
} from 'common/userRoles';

import {
  DATA_TABLE_SOURCE_PARTNER_HOUSEHOLD_MOVE_INS,
  DATA_TABLE_VIEW_ID_MARKETING_PARTNER_LISTINGS_ALL,
  DATA_TABLE_VIEW_ID_MARKETING_PARTNER_LISTINGS_REFERRAL,
} from 'common/dataTableConstants';

import {
  MARKETING_PARTNER_LISTING_AGREEMENT_SIGNED_BY_SUNROOM,
} from 'common/marketingPartnerListingConstants';

import {
  dataTable,
  actionDataTableLoadNextPage,
} from 'client/helpers/dataTables';

import withInfiniteScroll from 'client/helpers/withInfiniteScroll';
import branch from 'client/core/branch';

import SrText from 'client/components/SrText';
import SrPartnerListingCard, {
  SR_PARTNER_LISTING_CARD_TAG_THEME_WHITE,
  SR_PARTNER_LISTING_CARD_TAG_THEME_WHITE_BLACK,
} from 'client/components/SrPartnerListingCard';
import SrPartnerList from 'client/components/SrPartnerList';
import SrPartnerSection from 'client/components/SrPartnerSection';
import SrDataRetrievalText from 'client/components/SrDataRetrievalText/SrDataRetrievalText';
import SrHouseholdUpcomingMoveInCard from 'client/components/SrHouseholdUpcomingMoveInCard/SrHouseholdUpcomingMoveInCard';
import SrUserSettingDropdownWithSearchBar from 'client/components/SrUserSettingDropdownWithSearchBar/SrUserSettingDropdownWithSearchBar';

const getFilterOptions = (props) => {
  return lodashCompact([
    props.displayFilters && {
      label: 'All',
      value: DATA_TABLE_VIEW_ID_MARKETING_PARTNER_LISTINGS_ALL,
    },
    props.displayFilters && {
      label: 'Owner Listings',
      value: DATA_TABLE_VIEW_ID_MARKETING_PARTNER_LISTINGS_REFERRAL,
    },
  ]);
};

const SR_MARKETING_PARTNER_VIEW_ID_TO_FILTERS_MAPPING = {
  [DATA_TABLE_VIEW_ID_MARKETING_PARTNER_LISTINGS_REFERRAL]: {
    listingAgreementSignedBy: MARKETING_PARTNER_LISTING_AGREEMENT_SIGNED_BY_SUNROOM,
  },
};

function getDataTableSettingsFromProps(props) {
  const rootViewId = props.marketingListingsViewId;
  const filters = SR_MARKETING_PARTNER_VIEW_ID_TO_FILTERS_MAPPING[rootViewId];
  const viewIdSuffix = lodashCompact([
    rootViewId,
    props.partner?.id || '',
    props.listingId,
    props.upcoming ? 'upcoming' : '',
  ]).join('_');
  const marketIds = props.upcoming
    ? props.currentUser.settings.partnerPortal?.upcomingMoveIns?.[props.partner?.id + '_marketIds']
    : props.currentUser.settings.partnerPortal?.pastMoveIns?.[props.partner?.id + '_marketIds'];
  const viewId = `DATA_TABLE_VIEW_ID_HOUSEHOLD_MOVE_IN_${viewIdSuffix}`;

  const dataTableSettings = {
    dataSource: DATA_TABLE_SOURCE_PARTNER_HOUSEHOLD_MOVE_INS,
    viewId,
    filter: props.partner?.id ? { 'listings.partnerId': props.partner?.id } : {},
    count: 50,
    sort: {
      dateToSort: props.upcoming ? 'asc' : 'desc',
    },
    customParams: {
      partnerHouseholdMoveInsCustomParams: {
        upcoming: props.upcoming,
        excludeArchivedHouseholdListings: true,
      },
    },
  };

  if (props.displayFilters && filters) {
    dataTableSettings.filter = { ...dataTableSettings.filter, ...filters };
  }

  if (props.listingId) {
    dataTableSettings.filter.listingId = props.listingId;
  }

  if (marketIds && marketIds.length > 0 && !props.listingId) {
    dataTableSettings.customParams.partnerHouseholdMoveInsCustomParams.marketIds = marketIds;
    dataTableSettings.rootViewId = `${viewId}_marketIds:${marketIds.join('_')}`; // eslint-disable-line max-len
    dataTableSettings.viewId = `${viewId}_marketIds:${marketIds.join('_')}`; // eslint-disable-line max-len
  }

  return dataTableSettings;
}

@branch(() => ({
  marketingListingsViewId: ['partners', 'marketingListingsViewId'],
  totalListings: ['dataTables', DATA_TABLE_SOURCE_PARTNER_HOUSEHOLD_MOVE_INS],

}))
@dataTable(getDataTableSettingsFromProps)
@withInfiniteScroll({
  offset: 80,
})
class SrDataTablePartnerHouseholdMoveIns extends Component {
  static propTypes = {
    partner: PropTypes.object, // eslint-disable-line react/no-unused-prop-types
    currentUser: PropTypes.object.isRequired,
    items: PropTypes.array.isRequired,
    dispatch: PropTypes.func.isRequired,
    dataTableApiState: PropTypes.object.isRequired,
    dataTableIsFullyLoaded: PropTypes.bool.isRequired,
    onInfiniteScrollWrapperRef: PropTypes.func.isRequired,
    onInfiniteScrollCallbackReady: PropTypes.func.isRequired,
    upcoming: PropTypes.bool.isRequired,
    withBlankState: PropTypes.bool,
    listingId: PropTypes.number, // eslint-disable-line react/no-unused-prop-types
    useSrHouseholdUpcomingMoveInCard: PropTypes.bool,
    showPartnerName: PropTypes.bool,
    showUninstallOrderDetails: PropTypes.bool,
    marketingListingsViewId: PropTypes.string,
    accountManagerId: PropTypes.number,
  }

  constructor(props) {
    super(props);

    const { onInfiniteScrollCallbackReady } = props;
    onInfiniteScrollCallbackReady(this._infiniteScrollCallback);
  }

  UNSAFE_componentWillMount() {
    const { partnerListingsViewId } = this.props;
    if (!partnerListingsViewId) {
      this._onFilterClick(DATA_TABLE_VIEW_ID_MARKETING_PARTNER_LISTINGS_ALL);
    }
  }

  _infiniteScrollCallback = () => {
    const { dataTableIsFullyLoaded, dispatch } = this.props;
    if (!dataTableIsFullyLoaded) {
      dispatch(
        actionDataTableLoadNextPage,
        getDataTableSettingsFromProps(this.props),
      );
    }
  }

  _onFilterClick = (viewId) => {
    const { dispatch } = this.props;
    dispatch(({ state }) => {
      state.set(['partners', 'marketingListingsViewId'], viewId);
      state.commit();
    });
  }

  _getBottomChips = (listing) => {
    const label = [
      `${listing.addressCity},`,
      listing.addressStateCode,
    ].join(' ');

    return [
      {
        label,
        theme: SR_PARTNER_LISTING_CARD_TAG_THEME_WHITE_BLACK,
      },
    ];
  }

  render() {
    const {
      items: householdMoveIns,
      onInfiniteScrollWrapperRef,
      dataTableIsFullyLoaded,
      dataTableApiState,
      withBlankState,
      upcoming,
      showPartnerName,
      useSrHouseholdUpcomingMoveInCard,
      currentUser,
      dispatch,
      showUninstallOrderDetails,
      marketingListingsViewId,
      accountManagerId,
      totalListings,
      partner,
      listingId,
    } = this.props;

    const isCurrentUserAnonymous = hasRoleAnonymous(currentUser.roles);
    const isCurrentUserAdmin = hasRoleAdmin(currentUser.roles);
    const householdMoveInsFilter = householdMoveIns.filter((householdMoveIn) => {
      if (!accountManagerId) return true;

      return Number(accountManagerId) === householdMoveIn.partner?.accountManagerUserId;
    });

    if (dataTableIsFullyLoaded
      && ((!withBlankState && totalListings.length === 0)
      || (householdMoveInsFilter.length === 0 && listingId))
    ) {
      return null;
    }

    return (
      <div className='SrDataTablePartnerHouseholdMoveIns' ref={onInfiniteScrollWrapperRef}>
        <SrPartnerSection
          title={upcoming
            ? `Upcoming Move-In${householdMoveInsFilter.length > 1 ? 's' : ''}`
            : 'Past Move-Ins'
          }
          headerRow={
            <div className='partnerDataTableFilterContainer flex' style={{ lineHeight: '40px' }}>
              {partner && partner.markets?.length > 1 && !listingId && (
                <SrUserSettingDropdownWithSearchBar
                  title='Market'
                  notFoundTitle='No markets found'
                  settingField={`partnerPortal.${upcoming
                    ? 'upcomingMoveIns'
                    : 'pastMoveIns'
                  }.${partner.id}_marketIds`}
                  dropdownOptions={partner.markets.map((option) => ({
                    label: option.displayNameFull,
                    value: option.marketId,
                  }))}
                />
              )}
              {getFilterOptions(this.props).map(({ label, value }) => {
                const isActive = value === marketingListingsViewId;
                return (
                  <div key={value} style={{ margin: '0 0 0 25px' }}>
                    <SrText
                      size='large'
                      weight='medium'
                      color='blueGreycliff'
                      cursor={isActive ? null : 'pointer'}
                      decoration={isActive ? 'underline-yellow' : null}
                      font='greycliff'
                      opacity={isActive ? null : 0.5}
                      onClick={isActive ? null : () => this._onFilterClick(value)}
                      className='partnerDataTableFilterOption'
                    >
                      {label}
                    </SrText>
                  </div>
                );
              })}
            </div>
          }
        >
          <SrPartnerList>
            {dataTableIsFullyLoaded && householdMoveInsFilter.length === 0 && (
              <SrDataRetrievalText
                text={upcoming
                  ? 'There are no upcoming move-ins.'
                  : 'No properties have been moved into yet.'
                }
              />
            )}
            {householdMoveInsFilter.map((householdMoveIn) => {
              const itemKey = [
                householdMoveIn.householdId,
                householdMoveIn.listingId,
                householdMoveIn.partnerId,
              ].join('-');

              const listingFieldsForDisplay = getListingFieldsForDisplay({
                listing: householdMoveIn,
              });

              const stats = [
                `$${numberWithCommas(listingFieldsForDisplay.price)}`,
                bedsDisplayString(listingFieldsForDisplay),
                bathsDisplayString(listingFieldsForDisplay),
                `${numberWithCommas(listingFieldsForDisplay.squareFeet)} ft`,
              ];
              const addressStateDisplay = [
                `${householdMoveIn.addressCity},`,
                householdMoveIn.addressStateCode,
                householdMoveIn.addressZip,
              ].join(' ');

              return ((upcoming || useSrHouseholdUpcomingMoveInCard)
                ? (
                  <SrHouseholdUpcomingMoveInCard
                    key={itemKey}
                    addressStreetDisplay={householdMoveIn.addressStreetDisplay}
                    addressStateDisplay={addressStateDisplay}
                    appAcceptedDate={householdMoveIn.appAcceptedDate}
                    leaseSignedDate={householdMoveIn.leaseSignedDate}
                    securityDepositLabel={getMarketSecurityDepositLabel(householdMoveIn, true)}
                    moveInDate={householdMoveIn.moveInDate}
                    listingId={householdMoveIn.listingId}
                    listingFeeSettings={householdMoveIn.listingFeeSettings}
                    partner={householdMoveIn.partner}
                    partnerSlug={householdMoveIn.partnerSlug}
                    householdId={householdMoveIn.householdId}
                    renters={householdMoveIn.renters}
                    guarantors={householdMoveIn.guarantors}
                    isCurrentUserAnonymous={isCurrentUserAnonymous}
                    householdApplications={householdMoveIn.householdApplications}
                    leadRenterId={householdMoveIn.leadRenterId}
                    securityDepositCollectionInstructions={
                      householdMoveIn.securityDepositCollectionInstructions
                      || householdMoveIn.partnerLevelSecurityDepositCollectionInstructions
                    }
                    stripeAccount={householdMoveIn.listingStripeAccount}
                    securityDepositCharge={householdMoveIn.securityDepositCharge}
                    securityDepositChargeConfirmedBy={householdMoveIn.securityDepositChargeConfirmedBy}
                    depositPaidDate={householdMoveIn.depositPaidDate}
                    leaseSentDate={householdMoveIn.leaseSentDate}
                    isCurrentUserAdmin={isCurrentUserAdmin}
                    leaseStartDate={householdMoveIn.leaseStartDate}
                    leaseDocument={householdMoveIn.leaseDocument}
                    householdListing={householdMoveIn.householdListing}
                    dispatch={dispatch}
                    currentUser={currentUser}
                    showPartnerName={showPartnerName}
                    sunlockLockCode={householdMoveIn.sunlockLockCode}
                    lockboxCode={householdMoveIn.lockboxCode}
                    propertyAccessCode={householdMoveIn.propertyAccessCode}
                    propertySetupAccessType={householdMoveIn.propertySetupAccessType}
                    jobOrders={householdMoveIn.jobOrders}
                    showUninstallOrderDetails={showUninstallOrderDetails}
                  />
                )
                : (
                  <SrPartnerListingCard
                    key={itemKey}
                    to={`/partner/${householdMoveIn.partnerSlug}/listing/${householdMoveIn.listingId}`}
                    photos={[householdMoveIn.listingSunroomPhotoUrls[0]]}
                    tags={[
                      {
                        theme: SR_PARTNER_LISTING_CARD_TAG_THEME_WHITE,
                        label: moment(householdMoveIn.leaseStartDate || householdMoveIn.moveInDate)
                          .format('MMM D, YYYY'),
                      },
                    ]}
                    title={householdMoveIn.addressStreetDisplay}
                    stats={stats}
                    bottomChips={this._getBottomChips(householdMoveIn)}
                  />
                )
              );
            })}
          </SrPartnerList>
          {dataTableApiState.inProgress && (
            <div className='loading-ellipses' />
          )}
        </SrPartnerSection>
      </div>
    );
  }
}

SrDataTablePartnerHouseholdMoveIns.displayName = 'SrDataTablePartnerHouseholdMoveIns';

export default SrDataTablePartnerHouseholdMoveIns;
